import React from 'react';
import { ApplicationState } from '../../store/rootReducer';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Configuration, ConfigurationMap } from '../../types/rest/configurationsType';
import BlazeTable from '../../components/blazeTable/BlazeTable';
import {
  setCurrentConfiguration,
  setExpandedConfiguration,
  setIsCreatingConfiguration,
  setIsRecentlySaved,
} from '../../store/configurations/configurationsReducer';
import _ from 'lodash';
import { BlazeTableColDef, BlazeTableOptions } from '../../components/blazeTable/BlazeTable.types';
import {
  cloneConfiguration,
  createOrUpdateConfiguration,
  deleteConfiguration,
  getConfigurationsListAction,
} from '../../store/configurations/configurationsActions';
import clsx from 'clsx';
import BzmIcon from '../../components/icons/BzmIcon';
import ConfigurationFieldCell from './Cells/ConfigurationFieldCell';
import { Validator, ValidatorType } from '../../hooks/useFormInput';
import i18n from '../../i18n';
import ConfigurationDetail from './ConfigurationDetail';
import { confirm } from '../../components/ui/modal/BlazeConfirmationModal';
import acApi from '../../axios/ac/acApi';

type ConfigurationsBlazeTableProps = {
  onRef?: any;
  onSelectionChange?: (selected: Configuration[]) => void;
};

type AllProps = StateProps & DispatchProps & WithTranslation & ConfigurationsBlazeTableProps;

class ConfigurationsBlazeTable extends React.Component<AllProps> {
  tableRef: any = React.createRef<BlazeTable>();

  /** Clears all selected rows — called by the parent after a bulk operation completes. */
  deSelectAll = () => {
    if (this.tableRef && this.tableRef.deSelectAll) {
      this.tableRef.deSelectAll();
    }
  };

  componentDidMount() {
    this.props.getConfigurationsListAction();
    if (this.props.onRef) {
      this.props.onRef(this);
    }
  }

  componentDidUpdate(prevProps: Readonly<AllProps>) {
    if (prevProps.expandedId != this.props.expandedId) {
      this.setState({ inInitialState: true, invalidField: false });
    }

    if (this.props.isRecentlySaved === true) {
      if (this.state.inInitialState === false) {
        this.setState({ inInitialState: true });
      }
      this.props.setIsRecentlySaved(false);
    }
  }

  toggleExpandDetail = async (id: number | null) => {
    if (this.props.expandedId === id) {
      this.props.setCurrentConfiguration(null);
      this.props.setExpandedConfiguration(null);
    } else {
      const currentConfig: Configuration = _.find(this.props.configurations, el => el.id === id);
      this.props.setExpandedConfiguration(id);
      this.props.setCurrentConfiguration(currentConfig);
    }
  };

  _getFilteredResults = () => {
    const configurations = _.cloneDeep(this.props.configurations).map(c => {
      return c.id === this.props.expandedId && this.props.currentConfiguration.id === c.id
        ? _.cloneDeep(this.props.currentConfiguration)
        : c;
    });

    if (this.props.isCreatingNewConfiguration) {
      configurations.unshift(this.props.newConfiguration);
    }
    //add filters when they ask us to implement them...
    return configurations;
  };

  state = {
    invalidField: false,
    inInitialState: false,
    saveActionWithDirtyName: false,
    saveActionWithDirtyProps: false,
  };

  setInvalidField = (invalid: boolean) => {
    if (this.state.invalidField !== invalid || this.state.inInitialState === true) {
      this.setState({ invalidField: invalid, inInitialState: false });
    }
  };

  saveConfiguration = (configurationMap: ConfigurationMap, validationFound: boolean) => {
    const config = this.props.currentConfiguration
      ? _.cloneDeep(this.props.currentConfiguration)
      : ({} as Configuration);
    config.configurationMap = configurationMap;
    let isDirty = false;
    // do not save if config has empty props or duplicated ones
    if (config.configurationMap[''] !== undefined || validationFound === true) {
      this.setState({ saveActionWithDirtyProps: true });
      isDirty = true;
    }
    // do not save if config doesn't have a name or already exists
    if (_.isNil(config.name) || config.name.trim() === '' || this.state.invalidField) {
      this.setState({ saveActionWithDirtyName: true });
      isDirty = true;
    }
    if (isDirty === false) {
      this.props.createOrUpdateConfiguration(config);
    }
  };

  ExpandDetailComponent = (rowData: Configuration) => {
    return (
      <ConfigurationDetail
        configuration={rowData}
        invalidField={this.state.invalidField}
        onSave={this.saveConfiguration}
        inInitialState={this.state.inInitialState}
        setInInitialState={(isInitial: boolean) => this.setState({ inInitialState: isInitial })}
        dirtyPropsFound={this.state.saveActionWithDirtyProps}
        resetDirtyPropsFound={() => this.setState({ saveActionWithDirtyProps: false })}
      />
    );
  };

  deleteRowClicked = async (configuration: Configuration) => {
    if (configuration.id === -1 || _.isNil(configuration.id)) {
      this.props.setIsCreatingConfiguration(false);
    } else {
      const countUsage = (await acApi.configurations.getCountUsage(configuration.id)).data;
      const message = countUsage.result.configurationUsageCount === 0 ? 'inlineDeleteWarn' : 'configurationIsUsed';
      const modalBody = (
        <div>
          {i18n.t('configurations:configurationsTable.' + message)}{' '}
          <strong className="text-break">{configuration.name}</strong>?
        </div>
      );
      confirm({
        confirmation: modalBody,
        yesOptionTitle: i18n.t('common:actions.delete'),
        title: i18n.t('configurations:configurationsTable.delete'),
      })
        .then(() => {
          //confirming deleting a configuration in use will send ?force=true.
          this.props.deleteConfiguration(configuration.id, countUsage.result.configurationUsageCount > 0);
        })
        .catch(() => {
          // User chose "No"
        });
    }
  };

  RightSideButtonsCell = (rowData: Configuration) => {
    const { t } = this.props;
    const deleteBtn = (rowData: Configuration) => (
      <button
        id={`configurationDeleteButton` + rowData.id}
        className="btn btn-link-dark btn-no-hover"
        disabled={false}
        onClick={() => this.deleteRowClicked(rowData)}
        title={t('configurationsTable.deleteConfiguration')}
      >
        <BzmIcon variant={'icon-bzm-delete'} className={'icon-xs'} />
      </button>
    );
    const cloneBtn = (rowData: Configuration) => (
      <button
        id={`qa-tbl-row-clone-configurations-${rowData.id}`}
        className="btn btn-link-dark btn-no-hover"
        title={i18n.t('configurations:configurationsTable.cloneConfiguration')}
        onClick={() => this.props.cloneConfiguration(rowData)}
      >
        <BzmIcon variant={'icon-bzm-copy'} className={'icon-xs'}></BzmIcon>
        <span className="sr-only">${i18n.t('configurations:configurationsTable.cloneConfiguration')}</span>
      </button>
    );

    return (
      <>
        <span className={clsx('ml-2', 'flex-table-actions')}>
          {rowData.id !== -1 && cloneBtn(rowData)}
          {deleteBtn(rowData)}
        </span>
      </>
    );
  };

  updateConfigurationField = (rowData: Configuration, field: 'name' | 'description', value: string) => {
    rowData[field] = value;
    this.props.setCurrentConfiguration(rowData);
  };

  getNameValidators = (names: string[]): Validator[] => {
    const nameValidators: Validator[] = [
      {
        type: ValidatorType.REQUIRED,
        invalidMessage: i18n.t('configurations:configurationsTable.validations.nameRequired'),
      },
      {
        type: ValidatorType.INVALID_CHARACTERS,
        invalidMessage: i18n.t('configurations:configurationsTable.validations.configurationIllegalChars'),
      },
      {
        type: ValidatorType.MAX_LENGTH,
        value: 250,
        invalidMessage: i18n.t('configurations:configurationsTable.validations.configurationMaximumLength'),
      },
      {
        type: ValidatorType.VALUE_FOUND_ON_ARRAY,
        value: names,
        invalidMessage: i18n.t('configurations:configurationsTable.validations.configurationNameAlreadyExists'),
      },
    ];
    return nameValidators;
  };

  getColsDefinition = () => {
    // Definition of table columns
    const colsDefinition: BlazeTableColDef[] = [
      {
        header: this.props.t('configurationsTable.columnNames.name'),
        width: 300,
        cellComponent: (rowData: Configuration) => {
          return (
            <ConfigurationFieldCell
              toggleExpandDetail={this.toggleExpandDetail}
              configuration={rowData}
              onChange={(rowData: Configuration, value: string) =>
                this.updateConfigurationField(rowData, 'name', value)
              }
              disabled={false}
              field={'name'}
              fieldValidator={this.getNameValidators(
                this.props.configurations
                  .filter(c => c.id !== _.get(this.props.currentConfiguration, 'id'))
                  .map(c1 => c1.name),
              )}
              isExpanded={this.props.expandedId === rowData.id && !_.isNil(this.props.currentConfiguration)}
              setInvalidField={this.setInvalidField}
              invalidFieldFound={this.state.saveActionWithDirtyName}
              resetDirtyFieldFound={() => this.setState({ saveActionWithDirtyName: false })}
              autoFocus={true}
            />
          );
        },
        cellClass: 'flex-table-col-overflow-visible',
        accessor: 'name',
      },
      {
        header: this.props.t('configurationsTable.columnNames.description'),
        cellComponent: (rowData: Configuration) => {
          return (
            <ConfigurationFieldCell
              toggleExpandDetail={this.toggleExpandDetail}
              configuration={rowData}
              onChange={(rowData: Configuration, value: string) =>
                this.updateConfigurationField(rowData, 'description', value)
              }
              disabled={false}
              field={'description'}
              fieldValidator={[]}
              isExpanded={this.props.expandedId === rowData.id}
              setInvalidField={this.setInvalidField}
              invalidFieldFound={false}
              resetDirtyFieldFound={() => _.noop()}
              autoFocus={false}
            />
          );
        },
        cellClass: 'flex-table-col-overflow-visible',
        accessor: 'description',
      },
      {
        header: this.props.t('configurationsTable.columnNames.actions'),
        cellComponent: (rowData: Configuration) => this.RightSideButtonsCell(rowData),
        cellClass: 'flex-table-col-overflow-visible',
        accessor: '',
        width: 80,
        isSortable: false,
      },
    ];

    return colsDefinition;
  };

  tableOptions: BlazeTableOptions = {
    expandDetailComponent: (rowData: any) => this.ExpandDetailComponent(rowData),
    showRowExpander: true,
    columnDef: this.getColsDefinition(),
    pageable: true,
    selectable: true,
    pageSize: 10,
    rowPadding: 8,
    customRowClassName: 'blazeTableRow',
    tableName: 'configurations',
    noResultsText: this.props.t('configurationsTable.noConfigurations'),
    disabled: false,
    sortable: true,
  };

  render() {
    return (
      <React.Fragment>
        <BlazeTable
          onExpandedItemChange={this.toggleExpandDetail}
          data={this._getFilteredResults()}
          options={this.tableOptions}
          isLoading={this.props.isLoading || this.props.isBulkDeleting}
          expandedItem={this.props.expandedId}
          onRef={(ref: any) => (this.tableRef = ref)}
          onSelectionChange={this.props.onSelectionChange}
          showPageSizeDropdown={true}
          pageSizeOptions={[5, 10, 25]}
        />
      </React.Fragment>
    );
  }
}
const mapStateToProps = ({ configurations }: ApplicationState) => ({
  isLoading: configurations.isLoading,
  isBulkDeleting: configurations.isBulkDeleting,
  configurations: configurations.configurations,
  expandedId: configurations.expandedId,
  isCreatingNewConfiguration: configurations.isCreatingNewConfiguration,
  currentConfiguration: configurations.currentConfiguration,
  newConfiguration: configurations.newConfiguration,
  isRecentlySaved: configurations.isRecentlySaved,
});

const mapDispatchToProps = {
  setExpandedConfiguration,
  getConfigurationsListAction,
  createOrUpdateConfiguration,
  setIsCreatingConfiguration,
  deleteConfiguration,
  setCurrentConfiguration,
  cloneConfiguration,
  setIsRecentlySaved,
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

export default withTranslation('configurations')(
  connect(mapStateToProps, mapDispatchToProps)(ConfigurationsBlazeTable),
);
